import { z } from 'zod';
import { uuidv7 } from 'uuidv7';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import { TransactionStatus, TransactionType } from '../api/transaction-types';
import { transactionDisplayTypeSchema } from '../type/type-types';
import { useBudgetId } from '../../budget/budget-store';
import { useTimeZone } from '../../user/api/use-get-user';
import { useCreateTransactionApi } from '../api/use-create-transaction';
import { useUpdateTransactionApi } from '../api/use-update-transaction';
/**
 * Combined transaction schema with integrated validation rules
 */
export const transactionFormSchema = z
    .object({
    id: z.string().uuid(),
    budgetId: z.string().uuid(),
    accountId: z.string().uuid({ message: 'Please select an Account' }),
    date: z.instanceof(Date),
    payee: z.string().optional(),
    notes: z.string().optional(),
    bucketId: z.string().optional(),
    transferAccountId: z.string().uuid().optional(),
    transferTransactionId: z.string().uuid().optional(),
    externalTransactionId: z.string().optional(),
    inflow: z.coerce.number().optional(),
    outflow: z.coerce.number().optional(),
    upcoming: z.boolean().optional(),
    type: z.nativeEnum(TransactionType),
    status: z.nativeEnum(TransactionStatus),
    displayType: transactionDisplayTypeSchema,
})
    .superRefine((data, ctx) => {
    // Validate that a transaction doesn't have both inflow and outflow
    if ((data?.inflow || 0) !== 0 && (data?.outflow || 0) !== 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Cannot have both inflow and outflow',
            path: ['outflow'],
        });
    }
    // Validate that transfer source and destination accounts are different
    if (data.displayType.startsWith('transfer:')) {
        const [, targetAccountId] = data.displayType.split(':');
        if (data.accountId === targetAccountId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Transfer account cannot be the same as the source account',
                path: ['displayType'],
            });
        }
    }
});
/**
 * Hook to manage transaction form state and submission with proper timezone handling
 */
export const useTransactionForm = ({ transaction, accountId, onSuccess }) => {
    const budgetId = useBudgetId();
    const userTimeZone = useTimeZone();
    /**
     * Creates default values for a new transaction form
     *
     * @returns A new transaction form data object with fresh IDs
     */
    const createDefaultValues = useCallback(() => {
        if (!transaction) {
            return {
                id: uuidv7(),
                budgetId,
                accountId: accountId || '',
                date: DateTime.now().setZone(userTimeZone).startOf('day').toJSDate(),
                type: TransactionType.TRANSACTION,
                status: TransactionStatus.PENDING,
                payee: '',
                notes: '',
                displayType: 'inflow',
                transferTransactionId: uuidv7(),
                inflow: 0,
                outflow: 0,
            };
        }
        return toFormData(transaction, userTimeZone);
    }, [transaction, budgetId, accountId, userTimeZone]);
    const defaultValues = useMemo(() => createDefaultValues(), [createDefaultValues]);
    const form = useForm({
        resolver: zodResolver(transactionFormSchema),
        defaultValues,
    });
    useEffect(() => {
        form.reset(defaultValues);
    }, [defaultValues, form]);
    /**
     * Resets the form with fresh default values and new UUIDs
     */
    const resetForm = useCallback(() => {
        const freshValues = createDefaultValues();
        form.reset(freshValues);
    }, [form, createDefaultValues]);
    /**
     * Handles successful form submission
     * For new transactions, resets the form with fresh values
     * For existing transactions, just calls the onSuccess callback
     */
    const handleSuccess = useCallback(() => {
        if (!transaction) {
            resetForm();
        }
        if (onSuccess) {
            onSuccess();
        }
    }, [transaction, resetForm, onSuccess]);
    // Use our custom success handler that includes form reset
    const { mutate: mutateCreate, isPending: isCreatePending } = useCreateTransactionApi({
        onSuccess: handleSuccess,
    });
    const { mutate: mutateUpdate, isPending: isUpdatePending } = useUpdateTransactionApi({
        onSuccess: handleSuccess,
    });
    const handleSubmit = (values) => {
        const { displayType, date, transferTransactionId, ...baseData } = values;
        const type = getTypeFromDisplayType(displayType);
        const bucketId = getBucketIdFromDisplayType(displayType);
        const transferAccountId = getAccountIdFromDisplayType(displayType) || undefined;
        const dateEpoch = calculateTransactionTimestamp(date, userTimeZone, transaction);
        const requestData = {
            ...baseData,
            type,
            bucketId,
            transferAccountId,
            transferTransactionId: type === TransactionType.TRANSFER ? transferTransactionId || uuidv7() : undefined,
            dateEpoch,
        };
        if (transaction) {
            mutateUpdate(requestData);
        }
        else {
            mutateCreate(requestData);
        }
    };
    return {
        form,
        onSubmit: form.handleSubmit(handleSubmit),
        isSubmitting: isCreatePending || isUpdatePending,
    };
};
/**
 * Converts a Transaction object to TransactionFormData format.
 *
 * This function transforms a raw Transaction into the shape expected by the transaction form:
 *
 * - Converts `dateEpoch` (UTC) to a Date object representing local wall time in the user's timezone
 * - For transfers:
 *   - Sets `displayType` to "transfer:{accountId}"
 *   - Clears `transferAccountId` and `bucketId` (handled via displayType)
 * - For regular transactions:
 *   - Sets `displayType` to `bucketId` if present, otherwise "inflow"
 *   - Clears `bucketId` (it's now embedded in `displayType`)
 *
 * @param transaction - The Transaction to convert
 * @param timeZone - IANA timezone string to localize the date (e.g. "America/New_York")
 * @returns Form-ready transaction data
 */
function toFormData(transaction, timeZone) {
    const date = DateTime.fromSeconds(transaction.dateEpoch, { zone: 'utc' }).setZone(timeZone).toJSDate();
    if (transaction.type === TransactionType.TRANSFER && transaction.transferAccountId) {
        return {
            ...transaction,
            date,
            displayType: `transfer:${transaction.transferAccountId}`,
            transferAccountId: undefined,
            bucketId: undefined,
        };
    }
    return {
        ...transaction,
        date,
        displayType: transaction.type === TransactionType.ADJUSTMENT ? 'adjustment' : transaction.bucketId || 'inflow',
        bucketId: undefined,
    };
}
/**
 * Calculates the final Unix timestamp for a transaction based on whether it's new or existing,
 * and whether the date has changed.
 *
 * @param date - JavaScript Date object representing the selected date
 * @param userTimeZone - The user's timezone (e.g., 'America/New_York')
 * @param existingTransaction - Optional existing transaction data
 * @returns Unix timestamp (seconds since epoch) in UTC
 */
export function calculateTransactionTimestamp(date, userTimeZone, existingTransaction) {
    // For new transactions, use noon on the selected date in user's timezone
    if (!existingTransaction) {
        return DateTime.fromJSDate(date)
            .setZone(userTimeZone, { keepLocalTime: true })
            .set({ hour: 12, minute: 0, second: 0, millisecond: 0 })
            .toUTC()
            .toUnixInteger();
    }
    // For existing transactions, check if date has changed
    const originalDate = DateTime.fromSeconds(existingTransaction.dateEpoch).setZone(userTimeZone);
    const newDate = DateTime.fromJSDate(date).setZone(userTimeZone);
    // If date changed, use noon on the new date
    if (!originalDate.hasSame(newDate, 'day')) {
        return newDate.set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).toUTC().toUnixInteger();
    }
    // If date unchanged, preserve the original timestamp
    return existingTransaction.dateEpoch;
}
/**
 * Extracts the bucketId if the transaction type is a standard bucket transaction.
 *
 * @param {string | undefined} type - The transaction display type.
 * @returns {string | undefined} The bucketId or undefined if not applicable.
 */
export function getBucketIdFromDisplayType(type) {
    if (!type?.startsWith('transfer:') && type !== 'adjustment' && type !== 'inflow') {
        return type;
    }
    return undefined;
}
/**
 * Extracts the account ID if the transaction type is a transfer or payment.
 *
 * @param {string | undefined} type - The transaction type string.
 * @returns {string | null} The extracted account ID, or null if not applicable.
 */
export function getAccountIdFromDisplayType(type) {
    return type?.startsWith('transfer:') ? type.split(':')[1] : null;
}
/**
 * Determines the transaction type based on the display type.
 *
 * @param {string | undefined} type - The transaction display type.
 * @returns {TransactionType} Returns TRANSFER if the type starts with transfer:, otherwise returns TRANSACTION.
 */
export function getTypeFromDisplayType(type) {
    if (type?.startsWith('adjustment')) {
        return TransactionType.ADJUSTMENT;
    }
    if (type?.startsWith('transfer:')) {
        return TransactionType.TRANSFER;
    }
    return TransactionType.TRANSACTION;
}
